import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle } from 'lucide-react';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // No token = invalid link
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-pcc-background py-12 px-4">
        <Card className={`w-full max-w-md border-0 shadow-xl ${'bg-pcc-card border border-pcc-card-border'}`}>
          <CardContent className="pt-8 text-center">
            <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${'bg-pcc-red-soft'}`}>
              <AlertCircle size={32} className="text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-pcc-text mb-2">Lien invalide</h2>
            <p className="text-pcc-text-muted mb-6">
              Ce lien de réinitialisation est invalide ou a expiré.
            </p>
            <Button
              onClick={() => navigate('/forgot-password')}
              className="w-full h-12 bg-pcc-primary hover:bg-pcc-primary-dark text-white rounded-xl"
            >
              Demander un nouveau lien
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast({
        title: 'Erreur',
        description: 'Les mots de passe ne correspondent pas',
        variant: 'destructive',
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: 'Erreur',
        description: 'Le mot de passe doit contenir au moins 6 caractères',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    const result = await resetPassword(token, password);

    if (result.success) {
      setSuccess(true);
    } else {
      toast({
        title: 'Erreur',
        description: result.error,
        variant: 'destructive',
      });
    }

    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-pcc-background py-12 px-4">
        <Card className={`w-full max-w-md border-0 shadow-xl ${'bg-pcc-card border border-pcc-card-border'}`}>
          <CardContent className="pt-8 text-center">
            <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${'bg-pcc-teal-soft'}`}>
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-pcc-text mb-2">Mot de passe réinitialisé!</h2>
            <p className="text-pcc-text-muted mb-6">
              Votre mot de passe a été modifié avec succès. Vous pouvez maintenant vous connecter.
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="w-full h-12 bg-pcc-primary hover:bg-pcc-primary-dark text-white rounded-xl"
            >
              Se connecter
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-pcc-background py-12 px-4">
      <Card className={`w-full max-w-md border-0 shadow-xl ${'bg-pcc-card border border-pcc-card-border'}`}>
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-pcc-primary/10 rounded-2xl flex items-center justify-center">
              <Lock size={32} className="text-pcc-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-center text-pcc-text">
            Nouveau mot de passe
          </CardTitle>
          <CardDescription className="text-center text-pcc-text-muted">
            Choisissez un nouveau mot de passe pour votre compte
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
            <div className="space-y-2">
              <Label htmlFor="password">Nouveau mot de passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 rounded-xl pr-12"
                  data-testid="reset-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 ${'text-pcc-text-muted hover:text-pcc-text'}`}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="h-12 rounded-xl"
                data-testid="reset-confirm-password"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-12 bg-pcc-primary hover:bg-pcc-primary-dark text-white rounded-xl font-semibold"
              disabled={loading}
              data-testid="reset-submit"
            >
              {loading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
            </Button>
          </form>
          
          <div className="mt-6 text-center text-sm">
            <Link to="/login" className="text-pcc-primary hover:underline font-semibold">
              Retour à la connexion
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
